import {Injectable} from '@angular/core';
import {type BUID, MerchantService, type UserProfile, getActiveOrganisationId} from '@odin/odin-core';
import {UserAccessLevel} from '../models/user-access-level';
import type { AccessPermission } from 'libs/odin-core/src/lib/models/access-permissions';

@Injectable({ providedIn: 'root' })
export class RoleGuardService {
  constructor(
    private merchantService: MerchantService,
  ) {}

  /** Check permissions provided by roles */
  hasPermission(requiredPermission: string): boolean {
    if (this.hasAccessLevel(UserAccessLevel.Admin)) return true;

    // Get their permissions
    const permissions = this.getAccessPermissions();
    if (!permissions?.length) return false;

    // Check if their partner has the permission
    const partnerId = this.getPartnerId();
    const partnerHasPermission = permissions.some(p =>
      p.resourceType === 'PAR' &&
      p.resource === (partnerId ?? '') &&
      p.actions.includes(requiredPermission)
    )

    if (partnerHasPermission) return true;

    // Check if the current organisation has the permission
    const organisationId = this.getOrganisationId();

    const orgHasPermission = permissions.some(p =>
      p.resourceType === 'ORG' &&
      p.resource == organisationId &&
      p.actions.includes(requiredPermission)
    )

    if (orgHasPermission) return true;

    // Check if the current business unit has the permission
    const currentBuid = this.getCurrentBuid();

    const buidHasPermission = permissions.some(p =>
      p.resourceType === 'BUID' &&
      p.resource === (currentBuid?.id ?? '') &&
      p.actions.includes(requiredPermission)
    )

    return buidHasPermission;
  }

  private getProfile(): UserProfile {
    return this.merchantService.getUserprofile() ?? ({} as UserProfile);
  }

  /** Get the user's current merchant BUID (from localStorage) */
  private getCurrentBuid(): BUID | null {
      return this.merchantService.getCurrentBuid();
  }

  /** Get the active organisation id via the organisation mapping layer */
  private getOrganisationId(): string | null {
    const profile = this.getProfile();
    return getActiveOrganisationId(profile) || null;
  }

  /** Extract permission list safely */
  private getAccessPermissions(): AccessPermission[] {
    const profile = this.getProfile();
    return profile?.accessPermissions ?? [];
  }

  private getPartnerId(): string | null {
    const profile = this.getProfile();
    return profile?.partnerId ?? null;
  }

  /** Keep Monek Admins Winning with Access Levels */
  private hasAccessLevel(accessLevel: UserAccessLevel): boolean {
    const profile = this.getProfile();
    if (profile === null) return false;
    return profile.accessLevel == accessLevel || profile.accessLevel === UserAccessLevel.Admin;
  }
}